/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { connect, useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { navigateTo, isPhone, Popup, isWeb, navigationRef, injectConditionalArrayItems } from '@helpers';
import ColorWrapper from '@styles/colors';
import { webHeader, role, fullWidth } from '@components/frame';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { RESTRICTED_ROOT_ROUTES } from '@config/tenantRestrictions';

import { getAuthInfo } from '@reducers/user';
import { setSkipBottomSheetNavigation, resetSkipBottomSheetNavigation } from '@components/common/ScreenAnimations/helpers';
import { selectNonBottomSheetScreen, setCloseTargetId, clearBottomSheetStack } from '@reducers/v2/bottomSheet/bottomSheet.slice';
import { selectFeatureFlags } from '@reducers/user';
import { FeatureFlagsAvailable } from '@components/featureFlags';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { CLOSE_ALL_BOTTOM_SHEETS, BOTTOM_SHEET_ANIMATION_TIMING, TEST_IDS } from '@v2/pages/common/constants/base.constants';

import TouchableElement from '@components/common/touchableElement';
import ChangeTileSizeModal from '@components/wallboard/changeTileSize/index';
import GlobalAdd from '@components/globalAdd/index';

import UserImage from '@components/common/UserImage';
import { SupportDropdown } from './support';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import Selector from '@styles/selectors';
import { usePlanboardAvailable } from '@v2/pages/planboard/hooks/usePlanboardAvailable';
import { useDashboardAvailable } from '@v2/pages/dashboard/hooks/useDashboardAvailable';
import { ProfileDropdown } from './profile';
import {
  CHANGE_TILE_SIZE_POPUP_WIDTH,
  DASHBOARD_INITIAL_SCREEN,
  WEB_HEADER_COLORS,
  WEB_HEADER_ICON_SIZES,
  WEB_HEADER_LAYOUT,
  WEB_HEADER_ROUTE_NAMES,
  WEB_HEADER_ROUTE_PATHS,
  WEB_HEADER_STYLES_CONFIG,
  WIDE_SCREEN_BREAK_POINT,
} from './constants';

let interval = null;

const Header = (props) => {
  const {
    C,
    ColorsData: { updateCustom, custom, scheme },
    updateAppView,
    triggerOverlay,
    overlay,
    route,
    isFullScreen,
    setIsFullScreen,
    triggerAddMenu,
    addMenu,
    triggerSupportMenu,
    supportMenu,
  } = props;

  const isPlanboardAvailable = usePlanboardAvailable();
  const isDashboardAvailable = useDashboardAvailable();
  const dispatch = useAppDispatch();
  const nonBottomSheetScreen = useSelector(selectNonBottomSheetScreen);
  const featureFlags = useSelector(selectFeatureFlags);
  const isWelcomeTourEnabled = FeatureFlagsAvailable('general-welcome-tour', featureFlags);
  const canGoBack = navigationRef?.current?.navigation?.canGoBack();
  const isInsideBottomSheet = nonBottomSheetScreen !== null || !canGoBack;
  const activeRouteName = route?.name;
  const isRestrictedTenant = RESTRICTED_ROOT_ROUTES.includes(activeRouteName);

  const [bigIndex, setBigIndex] = useState(false);

  useEffect(() => {
    const detectFullScrin = () => {
      const full_screen_element = document.fullscreenElement;

      if (full_screen_element) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    };
    window.document.addEventListener('fullscreenchange', detectFullScrin);

    const mouseMove = () => {
      if (interval) {
        clearTimeout(interval);
      }
      setBigIndex(true);
      interval = setTimeout(() => {
        setBigIndex(false);
      }, WEB_HEADER_LAYOUT.bigIndexResetTimeoutMs);
    };
    window.document.addEventListener('mousemove', mouseMove);
    window.document.addEventListener('click', mouseMove);

    return () => {
      window.document.removeEventListener('fullscreenchange', detectFullScrin);
      window.document.removeEventListener('mousemove', mouseMove);
      window.document.removeEventListener('click', mouseMove);
    };
  }, []);

  const changeTheme = async () => {
    const base = custom === 'system' ? scheme : custom;
    let n = base === 'light' ? 'dark' : 'light';
    if (!n || !base) {
      n = scheme === 'light' ? 'dark' : 'light';
    }
    updateCustom(n);
    updateAppView();
  };

  const goToFullScreen = () => {
    if (isFullScreen) {
      window.document
        .exitFullscreen()
        .then(function () {})
        .catch(function (error) {});
    } else {
      window.document.body
        .requestFullscreen({ navigationUI: 'hide' })
        .then(function () {})
        .catch(function (error) {});
    }

    setIsFullScreen(!isFullScreen);
  };

  const getRouteUrl = (routeName) => WEB_HEADER_ROUTE_PATHS[routeName] || `/${routeName.toLowerCase()}`;

  const handleTabNavigation = (event, link) => {
    if (isWeb) {
      if (event.ctrlKey || event.metaKey) {
        const baseUrl = window.location.origin;
        const routePath = getRouteUrl(link);
        const newUrl = `${baseUrl}${routePath}`;
        window.open(newUrl, '_blank');
        return;
      }

      // Set the global flag synchronously before navigating from header (only if inside bottom sheet)
      if (isInsideBottomSheet) {
        setSkipBottomSheetNavigation(true);
        dispatch(setCloseTargetId(CLOSE_ALL_BOTTOM_SHEETS));
        // Single cleanup after all animations complete (with generous buffer to avoid races)
        // New sheets are protected by isFirstRender check in AnimatedBottomSheet
        setTimeout(() => {
          resetSkipBottomSheetNavigation();
          dispatch(setCloseTargetId(null));
          dispatch(clearBottomSheetStack());
        }, BOTTOM_SHEET_ANIMATION_TIMING * 2);
      }
    }

    triggerOverlay();

    navigateTo(link);
  };

  const tabs = [
    {
      name: i18n.t('Machines'),
      link: WEB_HEADER_ROUTE_NAMES.AUTHORIZED,
      selected: activeRouteName === WEB_HEADER_ROUTE_NAMES.AUTHORIZED,
    },
    ...injectConditionalArrayItems(isDashboardAvailable, {
      name: i18n.t('Dashboard'),
      link: WEB_HEADER_ROUTE_NAMES.DASHBOARD,
      params: { screen: DASHBOARD_INITIAL_SCREEN },
      selected: activeRouteName === WEB_HEADER_ROUTE_NAMES.DASHBOARD,
    }),
    ...injectConditionalArrayItems(isPlanboardAvailable && !isRestrictedForOperator(role, RESTRICTION_CATEGORIES.WEB_TABS, 'Planboard'), {
      name: i18n.t('Planboard'),
      link: WEB_HEADER_ROUTE_NAMES.PLANBOARD,
      selected: activeRouteName === WEB_HEADER_ROUTE_NAMES.PLANBOARD,
    }),
    ...injectConditionalArrayItems(!isRestrictedForOperator(role, RESTRICTION_CATEGORIES.WEB_TABS, 'Wallboard'), {
      name: i18n.t('Wallboard'),
      link: WEB_HEADER_ROUTE_NAMES.WALLBOARD,
      selected: activeRouteName === WEB_HEADER_ROUTE_NAMES.WALLBOARD,
    }),
    ...injectConditionalArrayItems(!isRestrictedForOperator(role, RESTRICTION_CATEGORIES.WEB_TABS, 'Search'), {
      name: i18n.t('Search'),
      link: WEB_HEADER_ROUTE_NAMES.SEARCH,
      selected: activeRouteName === WEB_HEADER_ROUTE_NAMES.SEARCH,
    }),
    ...injectConditionalArrayItems(isPhone, {
      name: i18n.t('Settings'),
      link: WEB_HEADER_ROUTE_NAMES.SETTINGS,
      selected: activeRouteName === WEB_HEADER_ROUTE_NAMES.SETTINGS,
    }),
  ];

  const color = isPhone ? WEB_HEADER_COLORS.menuApp : WEB_HEADER_COLORS.menuWeb;

  const isWallboardRoute = route?.name === WEB_HEADER_ROUTE_NAMES.WALLBOARD;
  const isWideScreen = fullWidth >= WIDE_SCREEN_BREAK_POINT;

  const {
    item: { borderRadius, height, width, backgroundColorHoveredNotPressed, backgroundColor },
    container: { gapNarrowScreen, gapWideScreen },
  } = WEB_HEADER_STYLES_CONFIG;

  const headerItemsGap = isWideScreen ? gapWideScreen : gapNarrowScreen;

  const ZoomButton = ({ style }) => {
    return isWallboardRoute ? (
      <View style={style}>
        <TouchableElement onPress={goToFullScreen}>
          {({ boxStyle, pressed, hovered }) => (
            <View
              style={[
                {
                  width,
                  height,
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignContent: 'center',
                  position: 'relative',
                  backgroundColor: C(hovered && !pressed ? backgroundColorHoveredNotPressed : backgroundColor),
                  borderRadius,
                },
                boxStyle,
              ]}
            >
              {isFullScreen ? (
                <Svg
                  icon="zoomOut"
                  width={WEB_HEADER_ICON_SIZES.zoom}
                  height={WEB_HEADER_ICON_SIZES.zoom}
                  color={WEB_HEADER_COLORS.unselectedLabel}
                  key="zoomOut_not_hovered"
                />
              ) : (
                <Svg
                  icon="zoom"
                  width={WEB_HEADER_ICON_SIZES.zoom}
                  height={WEB_HEADER_ICON_SIZES.zoom}
                  color={WEB_HEADER_COLORS.unselectedLabel}
                  key="zoom_not_hovered"
                />
              )}
            </View>
          )}
        </TouchableElement>
      </View>
    ) : null;
  };

  return (
    <View
      style={{
        width: '100%',
        height: webHeader,
        backgroundColor: C(color),
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: isFullScreen ? (bigIndex ? WEB_HEADER_LAYOUT.fullScreenActiveZIndex : 0) : WEB_HEADER_LAYOUT.fullScreenActiveZIndex,
      }}
    >
      <View
        style={[
          {
            width: '100%',
            height: webHeader,
            backgroundColor: C(color),
            paddingLeft: WEB_HEADER_LAYOUT.horizontalPadding,
            paddingRight: WEB_HEADER_LAYOUT.horizontalPadding,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
          isPhone && {
            borderBottomColor: C(WEB_HEADER_COLORS.hairlinePrimary),
            borderBottomWidth: StyleSheet.hairlineWidth,
          },
        ]}
      >
        {!isPhone && (
          <View style={{ flexDirection: 'row' }}>
            <View style={[{ width: WEB_HEADER_LAYOUT.leftLogoContainerWidth }]}>
              <TouchableElement onPress={(event) => handleTabNavigation(event, WEB_HEADER_ROUTE_NAMES.AUTHORIZED)}>
                {({ boxStyle }) => (
                  <View style={[boxStyle, { width: WEB_HEADER_LAYOUT.sLogoWidth }]}>
                    <Svg
                      key="baseS"
                      icon="S"
                      width={WEB_HEADER_LAYOUT.sLogoWidth}
                      height={WEB_HEADER_LAYOUT.sLogoHeight}
                      color={WEB_HEADER_COLORS.unselectedLabel}
                    />
                  </View>
                )}
              </TouchableElement>
            </View>
          </View>
        )}
        <View style={[{ flexDirection: 'row' }, isPhone && { width: '100%', justifyContent: 'space-between', height: '100%' }]}>
          {!isRestrictedTenant &&
            tabs.map((item) =>
              !item.hide ? (
                <View
                  key={item.name}
                  style={[
                    isPhone
                      ? { height: '100%' }
                      : { marginLeft: WEB_HEADER_LAYOUT.tabHorizontalMargin, marginRight: WEB_HEADER_LAYOUT.tabHorizontalMargin },
                  ]}
                >
                  {isPhone ? (
                    <TouchableElement
                      onPress={() => {
                        if (item.selected) return;
                        triggerOverlay();
                        navigateTo(item.link);
                      }}
                    >
                      {() => (
                        <View
                          style={[
                            {
                              width: WEB_HEADER_LAYOUT.phoneTabWidth,
                              justifyContent: 'center',
                              borderBottomColor: C(item.selected ? WEB_HEADER_COLORS.selectedPrimary : color),
                              borderBottomWidth: 1,
                              height: '100%',
                            },
                            isPhone && {
                              width: 'auto',
                            },
                            isWeb && { cursor: 'pointer' },
                          ]}
                        >
                          <Text
                            style={[
                              font['Small'],
                              {
                                textAlign: 'center',
                                color: C(item.selected ? WEB_HEADER_COLORS.selectedPrimary : WEB_HEADER_COLORS.unselectedLabel),
                              },
                            ]}
                          >
                            {item.name}
                          </Text>
                        </View>
                      )}
                    </TouchableElement>
                  ) : (
                    <Selector
                      onPress={(event) => {
                        if (item.selected) return;
                        handleTabNavigation(event, item.link);
                      }}
                      {...{ variant: 'WEB', text: item.name, selected: item.selected }}
                    />
                  )}
                </View>
              ) : null
            )}
        </View>
        {!isPhone && (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              width: WEB_HEADER_LAYOUT.rightClusterWidth,
              gap: headerItemsGap,
            }}
          >
            <ZoomButton />
            {!isRestrictedTenant && (
              <TouchableElement onPress={(event) => handleTabNavigation(event, WEB_HEADER_ROUTE_NAMES.SETTINGS)}>
                {({ boxStyle, pressed, hovered }) => (
                  <View
                    testID={TEST_IDS.webHeader.settingsButton}
                    style={[
                      {
                        width,
                        height,
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignContent: 'center',
                        position: 'relative',
                        backgroundColor: C(hovered && !pressed ? backgroundColorHoveredNotPressed : backgroundColor),
                        borderRadius,
                      },
                      boxStyle,
                    ]}
                  >
                    <Svg
                      icon="cog"
                      width={WEB_HEADER_ICON_SIZES.cog}
                      height={WEB_HEADER_ICON_SIZES.cog}
                      color={WEB_HEADER_COLORS.unselectedLabel}
                      key="cog_not_hovered"
                    />
                  </View>
                )}
              </TouchableElement>
            )}
            {isWallboardRoute && (
              <TouchableElement
                onPress={() => {
                  Popup({
                    Component: ChangeTileSizeModal,
                    width: CHANGE_TILE_SIZE_POPUP_WIDTH,
                  });
                }}
              >
                {({ boxStyle, pressed, hovered }) => (
                  <View
                    style={[
                      {
                        width,
                        height,
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignContent: 'center',
                        position: 'relative',
                        backgroundColor: C(hovered && !pressed ? backgroundColorHoveredNotPressed : backgroundColor),
                        borderRadius,
                      },
                      boxStyle,
                    ]}
                  >
                    <Svg
                      icon="tileSize"
                      width={WEB_HEADER_ICON_SIZES.tileSize.width}
                      height={WEB_HEADER_ICON_SIZES.tileSize.height}
                      color={WEB_HEADER_COLORS.unselectedLabel}
                    />
                  </View>
                )}
              </TouchableElement>
            )}
            <TouchableElement onPress={() => changeTheme()}>
              {({ boxStyle, pressed, hovered }) => (
                <View
                  style={[
                    {
                      width,
                      height,
                      justifyContent: 'center',
                      alignItems: 'center',
                      alignContent: 'center',
                      position: 'relative',
                      backgroundColor: C(hovered && !pressed ? backgroundColorHoveredNotPressed : backgroundColor),
                      borderRadius,
                    },
                    boxStyle,
                  ]}
                >
                  <Svg
                    icon="theme"
                    width={WEB_HEADER_ICON_SIZES.theme.width}
                    height={WEB_HEADER_ICON_SIZES.theme.height}
                    color={WEB_HEADER_COLORS.unselectedLabel}
                    key="theme_not_hovered"
                  />
                </View>
              )}
            </TouchableElement>
            {!isRestrictedTenant && isWelcomeTourEnabled && (
              <TouchableElement onPress={() => triggerSupportMenu(!supportMenu)}>
                {({ boxStyle, pressed, hovered }) => (
                  <View
                    style={[
                      {
                        width,
                        height,
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignContent: 'center',
                        position: 'relative',
                        backgroundColor: C(hovered && !pressed ? backgroundColorHoveredNotPressed : backgroundColor),
                        borderRadius,
                      },
                      boxStyle,
                    ]}
                  >
                    <Svg
                      icon="infoFilled"
                      width={WEB_HEADER_ICON_SIZES.infoFilled}
                      height={WEB_HEADER_ICON_SIZES.infoFilled}
                      color={WEB_HEADER_COLORS.unselectedLabel}
                    />
                  </View>
                )}
              </TouchableElement>
            )}
            {!isRestrictedTenant && !isRestrictedForOperator(role, RESTRICTION_CATEGORIES.WEB_TABS, 'GlobalAdd') && (
              <TouchableElement onPress={() => triggerAddMenu(!addMenu)}>
                {({ boxStyle, pressed, hovered }) => (
                  <View
                    style={[
                      {
                        width,
                        height,
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignContent: 'center',
                        position: 'relative',
                        backgroundColor: C(hovered && !pressed ? backgroundColorHoveredNotPressed : backgroundColor),
                        borderRadius,
                      },
                      boxStyle,
                    ]}
                  >
                    <Svg
                      icon="plusSmall"
                      width={WEB_HEADER_ICON_SIZES.plusSmall}
                      height={WEB_HEADER_ICON_SIZES.plusSmall}
                      color={WEB_HEADER_COLORS.unselectedLabel}
                    />
                  </View>
                )}
              </TouchableElement>
            )}
            <UserImage {...{ showAdd: false, size: WEB_HEADER_LAYOUT.userImageSize, onPress: () => triggerOverlay(!overlay) }} />
          </View>
        )}
      </View>
    </View>
  );
};

const Wrapper = (props) => {
  const [overlay, setOverlay] = useState(false);
  const [addMenu, setAddMenu] = useState(false);
  const [supportMenu, setSupportMenu] = useState(false);
  const [wallboardLevel, setWallboardLevel] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const triggerOverlay = (value) => setOverlay(value ? value : false);
  const triggerAddMenu = (value) => setAddMenu(value ? value : false);
  const triggerSupportMenu = (value) => setSupportMenu(value ? value : false);
  const triggerWallboardLevel = (value) => setWallboardLevel(value ? value : false);

  const { OriginalComponent } = props;

  return (
    <View style={{ height: '100vh', width: '100%', overflow: 'hidden' }}>
      <HeaderBlock
        {...{
          ...props,
          triggerOverlay,
          overlay,
          isFullScreen,
          setIsFullScreen,
          triggerAddMenu,
          addMenu,
          triggerSupportMenu,
          supportMenu,
          triggerWallboardLevel,
          wallboardLevel,
        }}
      />
      <View style={{ width: '100%', flex: 1 }}>
        {overlay && (
          <OverlayBlock
            {...{
              triggerOverlay,
              overlay,
            }}
          />
        )}
        {addMenu && <GlobalAdd {...{ triggerAddMenu }} />}
        {supportMenu && <SupportDropdown {...{ triggerSupportMenu }} />}
        <OriginalComponent {...{ ...props, isFullScreen }} />
      </View>
    </View>
  );
};

const OverlayBlock = ColorWrapper(ProfileDropdown);

const HeaderBlock = connect(
  ({ connector, machines: { wallboardSelectedLevel } }) => ({
    connector,
    wallboardSelectedLevel,
  }),
  {}
)(ColorWrapper(Header));
const WrapperBlock = connect(({ user }) => ({ user }), { getAuthInfo })(ColorWrapper(Wrapper));

export default (OriginalComponent) => {
  return (props) => <WrapperBlock {...{ ...props, OriginalComponent }} />;
};
